/**
 * Live-panel connectors.
 *
 * Each of these subscribes to `../live-store.ts` and renders the matching pure
 * panel. They exist so the SUBSCRIPTION sits below react-grid-layout in the
 * tree rather than above it: when the store ticks, only this component and its
 * one panel re-render. The page, `WidthProvider` and all ten `GridItem`s are
 * untouched.
 *
 * The panels themselves stay pure props-in components — they are the things
 * worth unit-testing, and they should not know a store exists.
 *
 * Every connector is memoised on its props, which are all stable (callbacks and
 * primitives from the page), so a page render caused by something unrelated —
 * a filter change, the 5s history refresh — does not cascade into them either.
 */
import { memo, type JSX } from 'react'
import { useLive } from './live-store.js'
import { LiveBadge } from './ui.js'
import { DemoCallPlayer } from './demo/DemoCallPlayer.js'
import type { SupervisorActions } from './actions.js'
import type { SummaryMetrics } from './types.js'
import {
  AgentsPanel,
  CallsPanel,
  KpiPanel,
  LiveQueuePanel,
  StateCountPanel,
  type CallControl,
} from './panels.js'

export const LiveKpiPanel = memo(function LiveKpiPanel({
  totals,
}: {
  totals: SummaryMetrics
}): JSX.Element {
  const { agents, calls } = useLive()
  return <KpiPanel agents={agents} calls={calls} totals={totals} />
})

export const LiveAgentsPanel = memo(function LiveAgentsPanel({
  actions,
  canForceLogout,
}: {
  actions: SupervisorActions
  canForceLogout: boolean
}): JSX.Element {
  const { agents, calls } = useLive()
  return (
    <AgentsPanel
      agents={agents}
      calls={calls}
      actions={actions}
      canForceLogout={canForceLogout}
     
    />
  )
})

export const LiveCallsPanel = memo(function LiveCallsPanel({
  queueNames,
  actions,
  onListen,
  controls,
}: {
  queueNames: Map<string, string>
  actions: SupervisorActions
  onListen?: ((callId: string) => void) | undefined
  controls?: ReadonlyArray<CallControl> | undefined
}): JSX.Element {
  const { calls } = useLive()
  return (
    <CallsPanel
      calls={calls}
      queueNames={queueNames}
      actions={actions}
      {...(controls ? { controls } : {})}
      {...(onListen ? { onListen } : {})}
    />
  )
})

export const LiveStateCountPanel = memo(function LiveStateCountPanel(): JSX.Element {
  const { agents } = useLive()
  return <StateCountPanel agents={agents} />
})

export const LiveQueueActivityPanel = memo(function LiveQueueActivityPanel({
  queueNames,
}: {
  queueNames: Map<string, string>
}): JSX.Element {
  const { agents, calls } = useLive()
  return <LiveQueuePanel agents={agents} calls={calls} queueNames={queueNames} />
})

/**
 * The toolbar's live/offline indicator.
 *
 * Its own subscriber so that "is anything flowing" can update every second
 * without the toolbar — and therefore the page — re-rendering to find out.
 */
export const LiveFeedBadge = memo(function LiveFeedBadge(): JSX.Element {
  const { agents, calls } = useLive()
  return <LiveBadge live={agents.length > 0 || calls.length > 0} />
})

/**
 * DEMO MODE ONLY (phase 1) — the listen-in player.
 *
 * Resolves the call from the live store rather than taking it as a prop, so the
 * page only has to hold the selected call ID. Holding the call object itself
 * would mean the page needed the live call list, which is exactly the coupling
 * this module removes.
 */
export const LiveCallMonitor = memo(function LiveCallMonitor({
  callId,
  onHide,
}: {
  callId: string | null
  onHide: () => void
}): JSX.Element {
  const { calls } = useLive()
  const call = callId ? calls.find((c) => c.callId === callId) : undefined
  // Read the clock imperatively rather than subscribing: the player only needs
  // the join offset once, when it opens. Subscribing would re-render the dialog
  // every second to recompute a value it has already used.
  const elapsedSec = call
    ? Math.max(0, Math.floor((Date.now() - new Date(call.startedAt).getTime()) / 1000))
    : 0

  return (
    <DemoCallPlayer
      callId={callId}
      agentName={call?.agentName ?? ''}
      callerCLI={call?.callerCLI ?? ''}
      // Join the audio where the call actually is, so monitoring picks up
      // mid-conversation the way an ARI snoop would.
      elapsedSec={elapsedSec}
      onHide={onHide}
    />
  )
})
